import { useRef, useState } from "react";

import MDBox from "components/MDBox";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import {
  InboundSaveButton,
  InboundDetailAddButton,
  InboundDetailEditButton,
} from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./receiving-schema";
import { detailRender, detailValidation, detailValues } from "./receiving-details-schema";
import Receiving from "./components/Receiving";
import ReceivingDetail from "./components/ReceivingDetail";

import { recvColumns } from "./columns";
import { Success, Error_, InfoDialog, LoadingDialog } from "ui-components/Alert";

import { getReceiving, editReceiving } from "services/data.inbound.service";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { statusObj, statusLabel } from "layouts/views/common/enum";
import ApplyAll from "../components/ApplyAll";
import {
  cancelDetail,
  deleteNewDetail,
  getTotalQuantity,
  recvSkuValidation_,
  serialValidation_,
  updateDetail,
} from "../common/common";
import { RecvCaption } from "../components/Caption";
import ReceivingDivisionGroup from "./components/ReceivingDivisionGroup";
import { getShowDivision } from "services/data.inbound.service";

function EditReceiving() {
  const location = useLocation();
  const navigate = useNavigate();
  const { recvCode, customerCode } = location.state;
  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState(headerValues);
  const [details, setDetails] = useState([]);
  const [click, setClick] = useState(false);
  const [uploadData, setUploadData] = useState([]);
  const [showDivision, setShowDivision] = useState(false);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const { isFetching } = useQuery({
    queryKey: ["recv", { recvCode }],
    queryFn: () => getReceiving({ recvCode }),
    onSuccess: (data) => {
      const { recvDetails, divisionQuantity, ...recvValues } = data.data.data;
      setUploadData(divisionQuantity);
      setFormValues({
        ...recvValues,
        status: statusLabel[recvValues.status],
        recvType: "MISC Receipt",
      });
      setDetails(recvDetails.map((detail) => ({ ...detail, status: statusLabel[detail.status] })));
    },
    onError,
  });
  useQuery({
    queryKey: ["show-division", customerCode],
    queryFn: () => getShowDivision({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setShowDivision(data.data.data),
    onError,
  });
  const editReceivingMutation = useMutation(editReceiving, {});

  const form = useForm({
    defaultValues: headerValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = form;

  const headerProps = {
    headerRender,
    customerCode,
    control,
    setValue,
    errors,
    mode: "EDIT",
  };

  const [vendorCode] = watch(["vendorCode"]);
  //const watched = watch()
  const detailProps = {
    details,
    setDetails,
    uploadData,
    setUploadData,
    showDivision,
    values: { customerCode, vendorCode },
  };

  const onSubmit = async () => {
    setClick(true);
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) {
      setClick(false);
      return;
    }

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid Receiving Details" onChange={setAlert} />);
      setClick(false);
      return;
    }

    const serialNumbersMap = new Map();
    const affectedDets = new Set();

    for (const det of details) {
      for (const serial of det.serialNumbers) {
        const { serialNumber } = serial;
        if (serialNumbersMap.has(serialNumber)) {
          affectedDets.add(det.lineNo);
          affectedDets.add(serialNumbersMap.get(serialNumber).lineNo);
        } else {
          serialNumbersMap.set(serialNumber, det);
        }
      }
    }
    const result = Array.from(affectedDets);
    if (result.length > 0) {
      setAlert(
        <Error_
          content={`Duplicate Serial Numbers in LineNo ${result.join(", ")}`}
          onChange={setAlert}
        />
      );
      setClick(false);
      return;
    }

    const notTally = details.filter(
      (x) => x.inbSerialNbrCtrl && x.quantity !== x.serialNumbers.length
    );
    if (notTally.length > 0) {
      setAlert(
        <Error_
          content={`Serial Numbers Count Not Same as Quantity in LineNo ${notTally.join(", ")}`}
          onChange={setAlert}
        />
      );
      setClick(false);
      return;
    }

    const { recvType, ...data } = getValues();

    const req = {
      ...data,
      divisionQuantity: JSON.stringify(uploadData),
      recvDetails: details.map((d) => ({ ...d, uom: d.uom.label, status: statusObj[d.status] })),
    };

    editReceivingMutation.mutate(
      { recvCode, req },
      {
        onSuccess: (data) => navigate("/inbound/receiving", { state: data.data.message }),
        onError,
        onSettled: () => setClick(false),
      }
    );
  };

  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <Receiving {...headerProps} />
        </form>
      </InboundHeaderCard>
      <MDBox sx={{ margin: "1rem 0" }} />

      <Detail {...detailProps} />
      <InboundSaveButton
        url="/inbound/receiving"
        onClick={onSubmit}
        disabled={details.length === 0 || isSubmitting || click}
      />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, uploadData, setUploadData, showDivision, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Cancel"],
      type: "actions",
    },
    ...recvColumns,
  ];

  const [alert, setAlert] = useState(null);
  const [serialAlert, setSerialAlert] = useState(null);

  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;
  const doRef = useRef(null);
  const invRef = useRef(null);

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || {
      ...detailValues,
      lineNo,
      status: "Open",
      newItem: true,
      vDONo: doRef.current,
      vInvNo: invRef.current,
    },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const [skuCode, recvWh, recvZone] = watch(["skuCode", "recvWh", "recvZone"]);

  const onSubmit = (data) => {
    setSerialAlert(null);
    const isSkuAnyDuplicate = recvSkuValidation_(details, data);
    if (isSkuAnyDuplicate) {
      setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
      return;
    }

    if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
      const errorMsg =
        data.quantity > data.serialNumbers.length
          ? `Not Enough Serial Number(${data.serialNumbers.length})`
          : `Too Many Serial Number(${data.serialNumbers.length})`;
      setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
      return;
    }
    const isSerialAnyDuplicate = serialValidation_(details, data);
    if (isSerialAnyDuplicate.isValid) {
      if (isSerialAnyDuplicate.type === "Single") {
        const errorMsg = "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return;
      } else if (isSerialAnyDuplicate.type === "Multiple") {
        const errorMsg =
          "Duplicate Serial Number " +
          isSerialAnyDuplicate.serialNumbers.join(", ") +
          " In LineNo " +
          isSerialAnyDuplicate.lineNos.join(", ");
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return;
      }
    }
    if (formValues === null) {
      doRef.current = data.vDONo;
      invRef.current = data.vInvNo;
      setDetails([...details, data]);

      reset();
      setAlert(<Success content="Added" onChange={setAlert} />);
    } else {
      setDetails(updateDetail(details, data));
      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };
  const onRowClick = (values) => {
    setSerialAlert(null);
    if (formValues !== null && isDirty) {
      const data = getValues();
      const isSkuAnyDuplicate = recvSkuValidation_(details, data);
      if (isSkuAnyDuplicate) {
        setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
        return false;
      }
      //check current serial number, if invalid then keep focus on
      if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
        const errorMsg =
          data.quantity > data.serialNumbers.length
            ? `Not Enough Serial Number(${data.serialNumbers.length})`
            : `Too Many Serial Number(${data.serialNumbers.length})`;
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return false;
      }
      const isSerialAnyDuplicate = serialValidation_(details, data);
      if (isSerialAnyDuplicate.isValid) {
        if (isSerialAnyDuplicate.type === "Single") {
          const errorMsg =
            "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return false;
        } else if (isSerialAnyDuplicate.type === "Multiple") {
          const errorMsg =
            "Duplicate Serial Number " +
            isSerialAnyDuplicate.serialNumbers.join(", ") +
            " In LineNo " +
            isSerialAnyDuplicate.lineNos.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return false;
        }
      }
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const handleDelete = (data) => {
    if (data.newItem === false) return;
    setDetails(deleteNewDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const handleCancel = (data) => {
    if (data.newItem === true) return;
    setDetails(cancelDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };

  const detailProps = {
    detailRender,
    values: { ...values, skuCode, recvWh, recvZone },
    control,
    errors,
    setValue,
    getValues,
    mode: "EDIT",
    serialAlert,
  };
  const totalQuantity = getTotalQuantity(details, "quantity");
  const caption = <RecvCaption totalRows={details.length} totalQuantity={totalQuantity} />;

  const applyAllProps = { formValues, setFormValues, setValue, details, setDetails };
  return (
    <>
      <InboundHeaderCard>
        {alert}
        <ApplyAll {...applyAllProps} />

        {dialog}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <ReceivingDetail {...detailProps} />
          {formValues === null ? (
            <InboundDetailAddButton />
          ) : (
            <InboundDetailEditButton
              disabled={formValues.status === "Cancel" || !isDirty}
              onClick={() => setFormValues(null)}
            />
          )}
        </form>

        <BasicTable2
          columns={columns}
          rows={details}
          caption={caption}
          onDelete={handleDelete}
          onCancel={handleCancel}
          onRowClick={onRowClick}
          mode="EDIT"
        />
      </InboundHeaderCard>
      {showDivision && (
        <ReceivingDivisionGroup
          details={details}
          uploadData={uploadData}
          setUploadData={setUploadData}
        />
      )}
    </>
  );
};

export default EditReceiving;
